import * as React from "react";
import { useState, useEffect } from "react";
import AspectRatio from "@mui/joy/AspectRatio";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Card from "@mui/joy/Card";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import DropZone from "./Utils/DropZone";
import { UserAuth } from "../context/AuthContext";
import { db, storage } from "../FirebaseConfig";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../FirebaseConfig";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";

interface SettingsProps {}

const Settings: React.FC<SettingsProps> = () => {
  const { userData } = UserAuth();
  const [data, setData] = useState({
    name: userData?.name || "",
    email: userData?.email || "",
    profilePic: userData?.profilePic || "",
  });
  const [uploadFile, setUploadFile] = useState<string>("");
  const [fileLoading, setFileLoading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(
    userData?.profilePic || process.env.PUBLIC_URL + "/assets/user.png"
  );
  const [progress, setProgress] = useState(0);

  const handlereset = async () => {
    try {
      if (userData?.email) {
        await sendPasswordResetEmail(auth, userData.email);
      } else {
        throw new Error("User email is undefined");
      }
      toast.success(
        `Password reset email sent to ${userData?.email} successfully`
      );
    } catch (error) {
      console.error("Error sending password reset email:", error);
      toast.error("Failed to send password reset email");
    }
  };

  useEffect(() => {
    setData({
      name: userData?.name || "",
      email: userData?.email || "",
      profilePic: userData?.profilePic || "",
    });
    setImagePreviewUrl(
      userData?.profilePic || process.env.PUBLIC_URL + "/assets/user.png"
    );
  }, [userData]);

  const handleFileChange = (file: File) => {
    if (!file) {
      toast.error("No file selected.");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      return;
    }

    const fileUrl = URL.createObjectURL(file);
    setImagePreviewUrl(fileUrl);

    setFileLoading(true);
    try {
      const fileRef = ref(
        storage,
        `profile-pictures/${userData?.email}_${file.name}`
      );
      const uploadTask = uploadBytesResumable(fileRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(progress);
        },
        (error) => {
          console.error("Error during file upload:", error);
          toast.error("Upload failed!");
          setFileLoading(false);
        },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            setData((prevData) => ({ ...prevData, profilePic: downloadURL }));
            setUploadFile(downloadURL);
            setFileLoading(false);
            toast.success("Profile picture uploaded successfully!");
          } catch (error) {
            console.error("Error getting download URL:", error);
            toast.error("Failed to get download URL.");
            setFileLoading(false);
          }
        }
      );
    } catch (error) {
      console.error("Unexpected error during file handling:", error);
      toast.error("An unexpected error occurred during upload.");
      setFileLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const updatedProfilePic = uploadFile || data.profilePic;

    try {
      const userDocRef = doc(db, "users", userData?.email || "defaultEmail");
      await updateDoc(userDocRef, {
        name: data.name,
        profilePic: updatedProfilePic,
      });

      toast.success("Profile Updated Successfully");

      // Update the userData with new details to persist changes across the app
      if (userData) {
        userData.name = data.name;
        userData.profilePic = updatedProfilePic;
      }

      // Reset uploadFile to prevent unnecessary updates
      setUploadFile("");

      // Refresh the page after 5 seconds to see the changes
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Profile update failed.");
    }
  };

  return (
    <Box sx={{ flex: 1, width: "100%" }}>
      <Stack
        spacing={4}
        sx={{
          display: "flex",
          mx: "auto",
          py: { xs: 2, md: 3 },
        }}
      >
        <Card>
          <Box sx={{ mb: 1 }}>
            <Typography level="title-md">Personal info</Typography>
            <Typography level="body-sm">
              Customize how your profile information will appear on the
              platform.
            </Typography>
          </Box>
          <Divider />
          <Stack
            spacing={3}
            sx={{ display: { xs: "none", md: "flex" }, my: 1 }}
          >
            <Stack
              spacing={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <AspectRatio
                ratio="1"
                maxHeight={200}
                sx={{
                  flex: 1,
                  minWidth: 160,
                  overflow: "hidden",
                  borderRadius: "100%",
                  maxWidth: 180,
                  alignContent: "center",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={imagePreviewUrl}
                  alt=""
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: "100%",
                    margin: "auto",
                    objectFit: "cover",
                  }}
                />
              </AspectRatio>
            </Stack>
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
              <FormLabel>Display Name</FormLabel>
              <FormControl
                sx={{ display: { sm: "flex-column", md: "flex-row" }, gap: 2 }}
              >
                <Input
                  size="sm"
                  placeholder="Display Name"
                  value={data.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setData({ ...data, name: e.target.value })
                  }
                />
              </FormControl>
              <FormControl sx={{ flexGrow: 1 }}>
                <FormLabel>Email Address</FormLabel>
                <Input
                  size="sm"
                  type="email"
                  startDecorator={<EmailRoundedIcon />}
                  placeholder="email"
                  value={data.email}
                  disabled
                  sx={{ flexGrow: 1 }}
                />
              </FormControl>
              <FormLabel>Profile Picture</FormLabel>
              <DropZone onFileSelected={handleFileChange} />
            </Stack>
          </Stack>
          <CardOverflow sx={{ borderTop: "1px solid", borderColor: "divider" }}>
            <CardActions
              sx={{
                pt: 2,
                justifyContent: "space-between",
              }}
            >
              <Button
                onClick={handlereset}
                size="sm"
                variant="outlined"
                color="danger"
              >
                Reset Password
              </Button>
              <Box>
                <Button
                  component={Link}
                  to="/dashboard"
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  sx={{ ml: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={onSubmit}
                  sx={{ ml: 1 }}
                  disabled={fileLoading} // Disable the save button when uploading
                >
                  Save
                </Button>
              </Box>
            </CardActions>
          </CardOverflow>
        </Card>
      </Stack>
    </Box>
  );
};

export default Settings;
